'use client';

import { useMemo } from 'react';
import { Settings, Trash2, Zap } from 'lucide-react';
import { formatUnits } from 'viem';
import { cn } from '@/lib/utils';
import type { SeatData } from '@/components/grid';
import { formatCompactUsd } from '../../data/mock-prices';

/** Tax tokens we have a live USD oracle for. Same set used everywhere
 *  the lobby has a USD-only render contract (`OwnedTilesBar`,
 *  `BoardCanvas2D.formatSeatPriceLabel`, `TilePriceOverlay.formatPrice`)
 *  so the basket's deferral behaviour matches the rest of the surface. */
const CONVERTIBLE_TAX_TOKEN_SYMBOLS = new Set(['TAKEOVER', 'WETH', 'ETH']);

interface BasketBarProps {
  /** Full seat list — used to resolve selected indices to price data. */
  seats: SeatData[];
  /** Indices currently in the basket. */
  selectedIndices: Set<number>;
  /** Open the multi-seat checkout sheet. */
  onCheckout: () => void;
  /** Empty the basket (Clear button / Esc). */
  onClear: () => void;
  /** Tax-token decimals — live chain = 18, mock snapshot = 6. */
  priceDecimals?: number;
  /** USD price of one tax token — bar renders total as USD when > 0. */
  priceTokenPriceUsd?: number;
  /** Tax-token ticker (`'TAKEOVER'` / `'USDC'` / …). When the token is
   *  one of the convertible-to-USD tokens and `priceTokenPriceUsd <= 0`
   *  the total chip renders a skeleton instead of a fabricated `$0.00`
   *  (the previous fallback computed `tokens * 0`). */
  priceTokenSymbol?: string;
  /** Full-board "golden takeover" — every eligible tile is selected. Turns
   *  the whole bar gold, overriding the cyan/blue tones. */
  allSelectedGold?: boolean;
  className?: string;
}

/**
 * Floating "basket" bar that appears at the bottom-center of the lobby
 * whenever the user has one or more tiles shift-selected. Pattern mirrors
 * the classic app's bulk drawer entry point, collapsed to a compact pill
 * strip so it sits comfortably above the panel toggles.
 *
 * Switches to a cobalt-blue "Manage" affordance when every selected tile
 * is owned by the viewer, mirroring the bulk drawer's manage mode so the
 * user knows the next click opens bulk management instead of bulk
 * takeover. Mixed selections keep the cyan "Checkout" CTA — the bulk
 * acquire drawer itself surfaces a buyout / claim split when the basket
 * mixes occupied and vacant tiles, and the lobby wrapper routes any
 * all-own basket through {@link BulkActionDrawer}'s `manage` body.
 *
 * Hides entirely when the basket is empty to avoid chrome noise.
 */
export function BasketBar({
  seats,
  selectedIndices,
  onCheckout,
  onClear,
  priceDecimals = 6,
  priceTokenPriceUsd = 1,
  priceTokenSymbol,
  allSelectedGold = false,
  className,
}: BasketBarProps) {
  /** True while the bar is waiting on a USD price for a convertible tax
   *  token (TAKEOVER / WETH / ETH). The total chip renders a skeleton
   *  in this state rather than computing `tokens * 0 = $0.00`, which
   *  used to read as "you have nothing in your basket" while seats were
   *  in fact selected. */
  const isTotalPending =
    priceTokenPriceUsd <= 0 &&
    !!priceTokenSymbol &&
    CONVERTIBLE_TAX_TOKEN_SYMBOLS.has(priceTokenSymbol);

  const total = useMemo(() => {
    if (isTotalPending) return 0;
    let sum = 0;
    for (const seat of seats) {
      if (!selectedIndices.has(seat.seatIndex)) continue;
      const tokens = Number(formatUnits(seat.selfAssessedPrice, priceDecimals));
      if (!Number.isFinite(tokens) || tokens <= 0) continue;
      sum += tokens * priceTokenPriceUsd;
    }
    return sum;
  }, [seats, selectedIndices, priceDecimals, priceTokenPriceUsd, isTotalPending]);

  // Manage mode = every basketed seat belongs to the viewer. Computed by
  // intersecting `selectedIndices` against `seats` so we don't trip on
  // synthetic / out-of-range indices that the lobby pads onto small
  // boards (`useLobbySeats` returns synthetic seats for capacity > grid).
  const { count, manageMode } = useMemo(() => {
    let total = 0;
    let ownAll = true;
    let any = false;
    for (const seat of seats) {
      if (!selectedIndices.has(seat.seatIndex)) continue;
      total += 1;
      any = true;
      if (!seat.isOwn) ownAll = false;
    }
    return { count: total, manageMode: any && ownAll };
  }, [seats, selectedIndices]);

  if (count === 0) return null;

  // Tone precedence: full-board gold > manage (own) blue > buyout cyan.
  const tone = allSelectedGold ? 'gold' : manageMode ? 'manage' : 'cyan';
  const t = <T,>(g: T, b: T, c: T): T =>
    tone === 'gold' ? g : tone === 'manage' ? b : c;

  return (
    <div
      className={cn(
        'flex items-center gap-2 p-1.5 rounded-[6px]',
        'bg-black/70 backdrop-blur-md border',
        t(
          'border-amber-400/55 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9),0_0_0_1px_rgba(251,191,36,0.28)]',
          'border-blue-400/45 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9),0_0_0_1px_rgba(96,165,250,0.18)]',
          'border-cyan-400/45 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9),0_0_0_1px_rgba(103,232,249,0.15)]',
        ),
        className,
      )}
      role="region"
      aria-label={`${count} seat${count === 1 ? '' : 's'} in basket`}
    >
      <span className={cn('pl-2 pr-1 flex items-center gap-2', t('text-amber-100', 'text-blue-100', 'text-cyan-100'))}>
        <span className={cn('text-[9px] font-mono tracking-[0.22em] uppercase', t('text-amber-200/80', 'text-blue-200/70', 'text-cyan-200/70'))}>
          {t('All in', 'Yours', 'Basket')}
        </span>
        <span className={cn(
          'inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-[3px] border text-[11px] font-mono font-semibold tabular-nums',
          t('bg-amber-400/20 border-amber-300/45', 'bg-blue-400/20 border-blue-300/40', 'bg-cyan-400/20 border-cyan-300/40'),
        )}>
          {count}
        </span>
      </span>

      <span className={cn('h-5 w-px', t('bg-amber-300/20', 'bg-blue-300/15', 'bg-cyan-300/15'))} aria-hidden />

      {isTotalPending ? (
        <span
          aria-busy="true"
          aria-label="Basket total loading"
          className={cn(
            'inline-block h-3 w-12 rounded-[2px] animate-pulse pr-1',
            t('bg-amber-300/25', 'bg-blue-300/25', 'bg-cyan-300/25'),
          )}
        />
      ) : (
        <span className={cn(
          'text-[12px] font-mono font-semibold tabular-nums pr-1 whitespace-nowrap',
          t('text-amber-50', 'text-blue-50', 'text-cyan-50'),
        )}>
          {formatCompactUsd(total)}
        </span>
      )}

      <button
        type="button"
        onClick={onClear}
        title="Clear basket (Esc)"
        aria-label="Clear basket"
        className="h-7 w-7 flex items-center justify-center rounded-[3px] border border-white/10 bg-black/40 text-zinc-300 hover:text-red-200 hover:border-red-400/50 transition-colors"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>

      <button
        type="button"
        onClick={onCheckout}
        className={cn(
          'h-7 px-3 flex items-center gap-1.5 rounded-[3px] border text-[11px] font-mono font-semibold tracking-[0.15em] uppercase transition-colors',
          t(
            'bg-amber-400/30 border-amber-300/65 text-amber-50 hover:bg-amber-400/40',
            'bg-blue-400/25 border-blue-300/60 text-blue-50 hover:bg-blue-400/35',
            'bg-cyan-400/25 border-cyan-300/60 text-cyan-50 hover:bg-cyan-400/35',
          ),
        )}
      >
        {manageMode ? (
          <>
            <Settings className="h-3 w-3" />
            Manage
          </>
        ) : (
          <>
            <Zap className="h-3 w-3" />
            Checkout
          </>
        )}
      </button>
    </div>
  );
}
